'use client';

import { useRef, useEffect, useState } from 'react';
import { gsap } from '@/lib/gsap';
import Image from 'next/image';
import Link from 'next/link';
import { Article, ArticleCategory, DEFAULT_ARTICLE_CATEGORIES } from '@/lib/types/admin';
import { getPublishedArticles } from '@/lib/services/articleService';
import { getActiveArticleCategories } from '@/lib/services/articleCategoryService';

export default function ArticlesPage() {
  const heroRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const homeTitleRef = useRef<HTMLHeadingElement>(null);
  const homeSubtitleRef = useRef<HTMLParagraphElement>(null);
  const homeSectionRef = useRef<HTMLElement>(null);

  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<{ value: string; label: string }[]>(DEFAULT_ARTICLE_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch articles and categories in parallel
        const [articlesData, categoriesData] = await Promise.all([
          getPublishedArticles(),
          getActiveArticleCategories(),
        ]);

        setArticles(articlesData);

        // Use fetched categories if available, otherwise keep defaults
        if (categoriesData.length > 0) {
          setCategories(categoriesData.map((c: ArticleCategory) => ({ value: c.value, label: c.label })));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!heroRef.current) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      // Hero is rendered visible by default for LCP; animate from a near-visible state.
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters && !prefersReducedMotion) {
        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
        shuffledLetters.forEach((letter, index) => {
          gsap.fromTo(
            letter,
            { scaleY: 0.5 },
            { duration: 0.5, scaleY: 1, ease: 'power3.out', delay: index * 0.04 },
          );
        });
        setTimeout(() => {
          applyHoverEffect(letters);
        }, 1000);
      } else if (letters) {
        applyHoverEffect(letters);
      }

      if (subtitleRef.current && !prefersReducedMotion) {
        gsap.fromTo(
          subtitleRef.current,
          { y: 16, opacity: 0.25 },
          { y: 0, opacity: 0.5, duration: 0.6, ease: 'power3.out' },
        );
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Animate articles grid when loaded
  useEffect(() => {
    if (!loading && gridRef.current) {
      const cards = gridRef.current.querySelectorAll('.article-card');
      gsap.set(cards, { y: 60, opacity: 0 });
      gsap.to(cards, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power3.out',
      });
    }
  }, [loading, selectedCategory]);

  // Animate filter buttons
  useEffect(() => {
    if (filterRef.current) {
      const buttons = filterRef.current.querySelectorAll('button');
      gsap.set(buttons, { y: 20, opacity: 0 });
      gsap.to(buttons, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        stagger: 0.05,
        delay: 1,
        ease: 'power3.out',
      });
    }
  }, []);

  // Animate HOME section on scroll
  useEffect(() => {
    if (!homeSectionRef.current) return;

    const ctx = gsap.context(() => {
      const letters = homeTitleRef.current?.querySelectorAll('.home-letter');

      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        gsap.to(letters, {
          scaleY: 1,
          opacity: 1,
          duration: 0.6,
          stagger: 0.05,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: homeSectionRef.current,
            start: 'top 80%',
            once: true,
            onEnter: () => {
              // Apply hover effect after animation completes
              setTimeout(() => {
                if (letters) applyHoverEffect(letters as NodeListOf<Element>);
              }, 600);
            },
          },
        });
      }

      // Animate subtitle
      if (homeSubtitleRef.current) {
        gsap.set(homeSubtitleRef.current, { y: 30, opacity: 0 });
        gsap.to(homeSubtitleRef.current, {
          y: 0,
          opacity: 0.5,
          duration: 0.7,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: homeSectionRef.current,
            start: 'top 80%',
            once: true,
          },
        });
      }
    }, homeSectionRef);

    return () => ctx.revert();
  }, []);

  const filteredArticles =
    selectedCategory === 'all'
      ? articles
      : articles.filter((a) => a.category?.toLowerCase().trim() === selectedCategory.toLowerCase().trim());

  const featuredArticle = articles.find((a) => a.featured) || articles[0];
  // Only exclude featured article from regular list when showing "all" (since featured is displayed separately)
  const regularArticles = selectedCategory === 'all'
    ? filteredArticles.filter((a) => a.id !== featuredArticle?.id)
    : filteredArticles;

  const title = 'ARTICLES';

  return (
    <div ref={heroRef}>
      {/* Hero Section */}
      <section
        className="relative flex min-h-screen flex-col items-center justify-center"
        style={{ backgroundColor: '#173835' }}
      >
        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="text-center text-sm font-medium uppercase tracking-widest text-white/50"
          style={{ marginBottom: '40px' }}
        >
          INSIGHTS & KNOWLEDGE
        </p>

        {/* Title */}
        <h1
          ref={titleRef}
          className="hero-title font-six-caps text-[calc(1rem+20vw)] leading-[0.9] text-white lowercase"
        >
          {title.split('').map((char, i) => (
            <span
              key={i}
              className="letter inline-block"
              style={{ transformOrigin: 'center bottom' }}
            >
              {char}
            </span>
          ))}
        </h1>

        {/* Bottom navigation */}
        <div className="absolute bottom-12 left-0 right-0 content-padding">
          <div className="flex items-center justify-between">
            {/* Scroll to Explore - Left */}
            <div className="flex items-center gap-3">
              <span className="text-sm tracking-wider text-white/50">Scroll to Explore</span>
              <svg
                className="h-4 w-4 text-white/50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {/* All Articles - Right */}
            <span className="text-sm tracking-wider text-white/50">
              {articles.length} Articles
            </span>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section style={{ backgroundColor: '#173835' }} className="py-10">
        <div className="content-padding">
          <div ref={filterRef} className="flex items-center justify-center gap-6 flex-wrap">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`text-sm tracking-wider transition-all duration-300 pb-1 ${
                selectedCategory === 'all'
                  ? 'text-white border-b border-white'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              ALL
            </button>
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`text-sm tracking-wider transition-all duration-300 pb-1 ${
                  selectedCategory === cat.value
                    ? 'text-white border-b border-white'
                    : 'text-white/40 hover:text-white/70'
                }`}
              >
                {cat.label.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && selectedCategory === 'all' && !loading && (
        <section style={{ backgroundColor: '#173835' }} className="py-20">
          <div className="content-padding">
            <div className="mb-8">
              <span className="text-sm uppercase tracking-widest text-white/40">Featured Article</span>
            </div>
            <Link
              href={`/articles/${featuredArticle.slug}`}
              className="group block"
            >
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden rounded-2xl">
                  {featuredArticle.coverImage ? (
                    <Image
                      src={featuredArticle.coverImage}
                      alt={featuredArticle.title}
                      fill
                      className="object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-white/5 flex items-center justify-center">
                      <svg className="w-24 h-24 text-white/10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#173835]/80 via-transparent to-transparent" />
                </div>

                {/* Content */}
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="px-4 py-1.5 bg-white/10 text-white/80 text-xs font-medium tracking-wider rounded-full uppercase">
                      {categories.find((c) => c.value === featuredArticle.category)?.label || featuredArticle.category}
                    </span>
                    <span className="text-white/40 text-sm">{featuredArticle.readTime} min read</span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight group-hover:text-white/80 transition-colors">
                    {featuredArticle.title}
                  </h2>
                  <p className="text-lg text-white/50 mb-8 leading-relaxed line-clamp-3">
                    {featuredArticle.excerpt}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                      <span className="text-white text-lg font-semibold">
                        {featuredArticle.author?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{featuredArticle.author}</p>
                      <p className="text-white/40 text-sm">Author</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section style={{ backgroundColor: '#173835' }} className="py-20">
        <div className="content-padding">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="w-16 h-16 border-2 border-white/10 border-t-white rounded-full animate-spin" />
            </div>
          ) : regularArticles.length > 0 ? (
            <>
              {selectedCategory === 'all' && (
                <div className="mb-12">
                  <span className="text-sm uppercase tracking-widest text-white/40">Latest Articles</span>
                </div>
              )}
              <div ref={gridRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {regularArticles.map((article) => (
                  <Link
                    key={article.id}
                    href={`/articles/${article.slug}`}
                    className="article-card group block"
                  >
                    <article className="h-full">
                      {/* Image */}
                      <div className="relative aspect-[16/10] overflow-hidden rounded-xl mb-6">
                        {article.coverImage ? (
                          <Image
                            src={article.coverImage}
                            alt={article.title}
                            fill
                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-white/5 flex items-center justify-center">
                            <svg className="w-16 h-16 text-white/10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                            </svg>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#173835]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>

                      {/* Meta */}
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-xs font-medium tracking-wider text-white/40 uppercase">
                          {categories.find((c) => c.value === article.category)?.label || article.category}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-white/20" />
                        <span className="text-xs text-white/40">{article.readTime} min</span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-semibold text-white mb-3 line-clamp-2 group-hover:text-white/80 transition-colors">
                        {article.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-white/40 text-sm leading-relaxed line-clamp-2 mb-6">
                        {article.excerpt}
                      </p>

                      {/* Author */}
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                          <span className="text-white/80 text-xs font-semibold">
                            {article.author?.charAt(0)}
                          </span>
                        </div>
                        <span className="text-white/40 text-sm">{article.author}</span>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-40">
              <h3 className="text-2xl font-medium text-white/60 mb-3">No articles yet</h3>
              <p className="text-white/40">
                {selectedCategory !== 'all'
                  ? 'No articles in this category.'
                  : 'New content coming soon.'}
              </p>
            </div>
          ) : null}
        </div>
      </section>

      {/* Datacode CTA Section */}
      <section
        ref={homeSectionRef}
        className="relative flex min-h-[70vh] flex-col items-center justify-center"
        style={{ backgroundColor: '#173835' }}
      >
        <p
          ref={homeSubtitleRef}
          className="text-center text-sm font-medium uppercase tracking-widest text-white/50 mb-10"
        >
          BACK TO WHERE IT ALL BEGINS
        </p>
        <Link href="/" className="group">
          <h2
            ref={homeTitleRef}
            className="font-six-caps text-[calc(1rem+15vw)] leading-[0.9] text-white lowercase"
          >
            {'DATACODE'.split('').map((char, i) => (
              <span
                key={i}
                className="home-letter inline-block"
                style={{ transformOrigin: 'center bottom' }}
              >
                {char.toLowerCase()}
              </span>
            ))}
          </h2>
        </Link>
      </section>

    </div>
  );
}
